import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import ReaderBanner from '../CustomBanner';
import ReachedLimitIcon from '../../assets/reachedLimit.svg';
import { User } from '../../types/User';
import { externalLinks, routes } from '../../constants';
import useRouter from '../../utils/useRouter';
import { useSelector } from 'react-redux';
import { getSubscription, getSubscriptionUpgradePreview } from '../../reducers/user.selectors';

interface VideoReaderBannerContainerProps {
    open: boolean;
    handleClose: () => void;
    user?: User;
}

const VideoReaderBannerContainer: React.FC<VideoReaderBannerContainerProps> = ({ open, handleClose, user }) => {
  const { t } = useTranslation();
  const { router } = useRouter();
  const subscriptionUpgradePreview = useSelector(getSubscriptionUpgradePreview);
  const subscription = useSelector(getSubscription);
  const isBasicUser = user?.isBasisUser && !subscription;
  const hasDocumentLevelAccess = user?.hasDocumentLevelAccess;
  const isEligibleForUpgrade = (!!subscriptionUpgradePreview?.allowToUpgrade || isBasicUser) && !hasDocumentLevelAccess;

  if (!user || !isEligibleForUpgrade) return null;

  const { isCourseUser, isLibraryUser, isSchoolUser } = user;
  const isLibraryOrSchoolCourseUser = isLibraryUser || (isSchoolUser && isCourseUser);

  const getVideoReaderBannerProps = () => {
    const subtitle = isLibraryOrSchoolCourseUser ?
      t('Your current subscription only allows limited access. Learn more about how to watch the full video.') :
      t(
        'Your current subscription only allows limited access. ' +
      'Upgrade now to watch the complete video and many more content.'
      );

    const button = isLibraryOrSchoolCourseUser
      ? {
        text: t('Learn more'),
        onClick: () => window.open(externalLinks.schoolLicenceOffer, '_blank'),
        icon: 'starBanner' as const,
      } : {
        text: t('Adjust plan'),
        onClick: () => router.push(`/${routes.upgradePackageSelection.root}`),
        icon: 'starBanner' as const,
      };
    return {
      Image: ReachedLimitIcon,
      title: t('You are at the limit'),
      subtitle,
      button,
    };
  };

  return (
    <Box sx={{
      position: 'absolute',
      height: '100%',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <ReaderBanner
        open={open}
        onClose={handleClose}
        {...getVideoReaderBannerProps()}
        dialogProps={{ 
          sx: { position: 'absolute' }
        }}
      />
    </Box>
  );
};

export default VideoReaderBannerContainer;
